import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { sectionVariant, staggerContainer, itemVariant, viewportOnce, ease } from "@/lib/motion";
import { usePageContent } from "@/hooks/usePageContent";

const fallback = {
  heroLabel: "Solutions",
  heroHeadline: "Growth solutions built around how your business actually scales.",
  heroDescription: "We don't sell isolated services. We design integrated growth systems aligned to your business model, stage, and constraints.",
  heroCta: "Discuss your challenge",
  heroSecondaryCta: "Explore solutions",
  identifiers: [
    { label: "Business Model", options: ["E-commerce", "SaaS", "D2C", "Enterprise"] },
    { label: "Primary Constraint", options: ["Demand quality", "Pipeline leakage", "Retention gaps", "Attribution blind spots"] },
    { label: "Internal Capability", options: ["Founder-led", "In-house team", "Agency-dependent", "Hybrid model"] },
    { label: "Primary Metric", options: ["CAC efficiency", "LTV growth", "MRR velocity", "Revenue predictability"] },
  ],
  identifiersTitle: "How to identify the right solution",
  solutions: [
    {
      id: "ecommerce", title: "E-commerce Growth",
      outcome: "Increase conversion efficiency, repeat purchase, and contribution margin.",
      solves: ["High acquisition costs with low return on ad spend", "One-time buyers not converting to repeat customers", "Margin compression from inefficient channel mix"],
      idealFor: "Brands doing $2M-$50M with product-market fit seeking sustainable scale.",
      metrics: ["CAC:LTV ratio", "Repeat purchase rate", "Contribution margin"],
      proof: "Designed for teams with strong product but fragmented marketing execution.",
      link: "/solutions/ecommerce-growth",
    },
    {
      id: "saas", title: "SaaS Acceleration",
      outcome: "Improve demand quality, pipeline velocity, and net revenue retention.",
      solves: ["High volume of leads with low qualification rates", "Long sales cycles and stalled pipeline", "Churn offsetting new customer gains"],
      idealFor: "B2B SaaS teams with $1M-$20M ARR ready to build predictable growth.",
      metrics: ["MQL-to-SQL rate", "Pipeline velocity", "Net revenue retention"],
      proof: "Optimized for companies with product traction but inconsistent demand.",
      link: "/solutions/saas-acceleration",
    },
    {
      id: "d2c", title: "D2C Scale",
      outcome: "Build channel diversification, brand equity, and profitable unit economics.",
      solves: ["Over-reliance on a single acquisition channel", "Brand awareness not translating to consideration", "CAC rising faster than customer value"],
      idealFor: "D2C brands transitioning from founder-led growth to systematic scale.",
      metrics: ["Channel contribution", "Brand search volume", "Blended ROAS"],
      proof: "Typically improves revenue predictability across channels within 90 days.",
      link: "/solutions/d2c-scale",
    },
    {
      id: "enterprise", title: "Enterprise Performance",
      outcome: "Drive transparency, optimization, and integration across marketing operations.",
      solves: ["Siloed teams with fragmented data and conflicting metrics", "Slow execution despite significant budgets", "Inability to attribute outcomes to investment"],
      idealFor: "Large organizations seeking agility and accountability in digital marketing.",
      metrics: ["Marketing efficiency ratio", "Speed to insight", "Cross-channel attribution"],
      proof: "Built for organizations with scale but constrained by legacy systems.",
      link: "/solutions/enterprise-performance",
    },
  ],
  decisionQuestions: [
    { question: "What's your business model?", options: ["E-commerce", "SaaS", "D2C", "Enterprise", "Other"] },
    { question: "Annual revenue range?", options: ["< $1M", "$1M - $5M", "$5M - $20M", "$20M+"] },
    { question: "Biggest growth bottleneck?", options: ["Demand quality", "Conversion", "Retention", "Attribution"] },
  ],
  decisionTitle: "Not sure which solution fits your current stage?",
  decisionDescription: "Answer a few questions and we'll point you in the right direction.",
  decisionCta: "Get a recommendation",
};

const SolutionCard = ({ solution, index }: { solution: any; index: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div className="group border-b border-border last:border-b-0" variants={itemVariant}>
      <button
        className="w-full py-8 md:py-10 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-3">
              <span className="text-xs text-muted-foreground font-medium tracking-wide">{String(index + 1).padStart(2, "0")}</span>
              <h3 className="text-xl md:text-2xl font-medium text-foreground group-hover:text-accent transition-colors duration-300">{solution.title}</h3>
            </div>
            <p className="text-muted-foreground text-base md:text-lg pl-10 md:pl-12">{solution.outcome}</p>
          </div>
          <motion.div className="mt-2 text-muted-foreground" animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3, ease }}>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </div>
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease }} className="overflow-hidden">
            <div className="pl-10 md:pl-12 pb-10 grid md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">What this solves</h4>
                <ul className="space-y-3">
                  {solution.solves.map((item: string, i: number) => (
                    <li key={i} className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-accent mt-2 shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">Ideal for</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{solution.idealFor}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">Key metrics</h4>
                  <div className="flex flex-wrap gap-2">
                    {solution.metrics.map((metric: string) => (
                      <span key={metric} className="text-xs px-3 py-1.5 bg-secondary text-secondary-foreground">{metric}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-between">
                <p className="text-sm text-muted-foreground/80 italic leading-relaxed mb-6">"{solution.proof}"</p>
                <Button variant="outline" size="sm" asChild className="self-start group/btn">
                  <Link to={solution.link}>Explore this solution<ArrowRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-1" /></Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Solutions = () => {
  const { content } = usePageContent("solutions", fallback);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 md:pt-28 lg:pt-32 pb-12 md:pb-20 lg:pb-24">
        {/* Hero Section */}
        <section className="container-wide mb-12 md:mb-20 lg:mb-32">
          <motion.div className="max-w-3xl" variants={sectionVariant} initial="hidden" animate="visible">
            <div className="flex items-center gap-3 mb-6">
              <div className="accent-bar" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">{content.heroLabel}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-semibold tracking-tight mb-6 leading-[1.15]">{content.heroHeadline}</h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 max-w-2xl">{content.heroDescription}</p>
            <div className="flex flex-wrap gap-4">
              <Button variant="primary" size="lg" asChild>
                <Link to="/contact" className="group">{content.heroCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              </Button>
              <Button variant="ghost" size="lg" asChild><a href="#solutions">{content.heroSecondaryCta}</a></Button>
            </div>
          </motion.div>
        </section>

        {/* How to Choose Section */}
        <section className="container-wide mb-12 md:mb-20 lg:mb-32">
          <motion.div variants={sectionVariant} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            <h2 className="text-sm font-semibold text-foreground mb-8 uppercase tracking-wide">{content.identifiersTitle}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
              {content.identifiers.map((item: any, index: number) => (
                <motion.div key={item.label} className="p-4 md:p-5 lg:p-6 bg-card border border-border" variants={itemVariant} custom={index}>
                  <h3 className="text-xs font-semibold text-accent mb-4 uppercase tracking-wide">{item.label}</h3>
                  <ul className="space-y-2">
                    {item.options.map((option: string) => (<li key={option} className="text-sm text-muted-foreground">{option}</li>))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Solutions Section */}
        <section id="solutions" className="container-wide mb-12 md:mb-20 lg:mb-32 scroll-mt-24">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            <div className="border-t border-border">
              {content.solutions.map((solution: any, index: number) => (
                <SolutionCard key={solution.id} solution={solution} index={index} />
              ))}
            </div>
          </motion.div>
        </section>

        {/* Decision Assist Section */}
        <section className="container-wide">
          <motion.div className="bg-card border border-border p-5 md:p-10 lg:p-16" variants={sectionVariant} initial="hidden" whileInView="visible" viewport={viewportOnce}>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4">{content.decisionTitle}</h2>
              <p className="text-muted-foreground mb-10">{content.decisionDescription}</p>
              <div className="grid sm:grid-cols-3 gap-6 mb-10 text-left">
                {content.decisionQuestions.map((item: any, index: number) => (
                  <div key={index} className="p-5 bg-secondary/50 border border-border">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">Question {index + 1}</span>
                    <p className="text-sm font-medium text-foreground">{item.question}</p>
                  </div>
                ))}
              </div>
              <Button variant="primary" size="lg" asChild>
                <Link to="/contact?intent=recommendation" className="group">{content.decisionCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              </Button>
            </div>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Solutions;
